import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';

import { commafy } from '../../../utils/methods';
import { _post } from '../../../utils/network';
import { contentLevelLabel, courseTypeLabel, courseTypeOptions, fetchCourseCategoryOptions, formatCoursePrice, getCourseType, toShamsiDate } from '../courseUtils';

import Breadcrumb from '../../../components/Breadcrumb/Breadcrumb.component';
import FormField from '../../../components/Form/FormField/FormField.component';
import LoadingButton from '../../../components/LoadingButton/LoadingButton.component';
import ListTable from '../../../components/List/ListTable/ListTable.component';
import Pagination from '../../../components/List/Pagination/Pagination.component';

const optionToBoolean = (value, trueValue, falseValue) => value === trueValue ? true : value === falseValue ? false : null;

const CoursesList = () => {
	const { t } = useTranslation();
	const [courses, setCourses] = useState([]);
	const [courseCategories, setCourseCategories] = useState([]);
	const [totalCount, setTotalCount] = useState(0);
	const [searchButtonIsLoading, setSearchButtonIsLoading] = useState(false);
	const [searchParams, setSearchParams] = useState({
		title: null,
		category_id: null,
		course_type: null,
		is_free: null,
		is_active: null,
		is_special: null,
		sort: null,
		page: 1,
		count: 10
	});

	const breadcrumbItems = [
		{ title: t('dashboard'), link: '/dashboard' },
		{ title: t('courses'), link: '/courses-list' },
		{ title: t('list') }
	];
	const pricingList = [
		{ key: t('free'), value: 'free' },
		{ key: t('paid'), value: 'paid' }
	];
	const activationsList = [
		{ key: t('active'), value: 'active' },
		{ key: t('deactive'), value: 'deactive' }
	];
	const specialsList = [
		{ key: t('normal'), value: 'normal' },
		{ key: t('special'), value: 'special' }
	];
	const sortsList = [
		{ key: t('newest'), value: 'date-desc' },
		{ key: t('oldest'), value: 'date-asc' },
		{ key: t('cheapest'), value: 'price-asc' },
		{ key: t('most_expensive'), value: 'price-desc' }
	];

	const coursesListData = useMemo(() => ({
		head: [t('creation_date'), t('title'), t('category'), t('course_type'), t('level'), t('price'), t('course_videos'), t('course_capacity'), t('course_remaining_capacity'), t('course_enrollments'), t('special'), t('status'), t('operation')],
		body: courses.map(course => [
			toShamsiDate(course.created_at),
			course.title,
			course.category_info?.title,
			courseTypeLabel(course, t),
			contentLevelLabel(course.level, t),
			<span className={`badge rounded-pill p-2 badge-subtle-${course.is_free ? 'success' : 'primary'}`}>
				{formatCoursePrice(course, t)}
			</span>,
			getCourseType(course) === 'in_person' ? '-' : commafy(course.videos_count ?? 0),
			getCourseType(course) === 'in_person' ? commafy(course.capacity ?? 0) : '-',
			getCourseType(course) === 'in_person' ? commafy(course.remaining_capacity ?? Math.max(0, (course.capacity ?? 0) - (course.enrollments_count ?? 0))) : '-',
			<Link to={`/course-enrollments-list?course_id=${course.id}`}>{commafy(course.enrollments_count ?? 0)}</Link>,
			<span className={`badge rounded-pill p-2 badge-subtle-${course.is_special ? 'warning' : 'secondary'}`}>
				{course.is_special ? t('special') : t('normal')}
			</span>,
			<span className={`badge rounded-pill p-2 badge-subtle-${course.is_active ? 'success' : 'secondary'}`}>
				{course.is_active ? t('active') : t('deactive')}
			</span>,
			<Link className='btn btn-secondary btn-sm p-1' to={`/courses-edit/${course.id}`}>
				<i className="fa fa-edit"></i> {t('edit')}
			</Link>
		]),
		directions: ['ltr', '', '', '', '', '', 'ltr', 'ltr', 'ltr', 'ltr', '', '', 'ltr']
	}), [courses, t]);

	const getCourseCategories = async () => {
		const { options, response } = await fetchCourseCategoryOptions();
		if(response.code !== 200)
			return toast.error(response[t('api_message')]);
		setCourseCategories(options);
	};

	const getCourses = async () => {
		setSearchButtonIsLoading(true);
		const response = await _post('/courses/admin/getCourses', searchParams);
		setSearchButtonIsLoading(false);
		switch(response.code){
			case 200:
				setTotalCount(response.data.courses_count);
				setCourses(response.data.courses);
				break;
			default:
				toast.error(response[t('api_message')]);
		}
	};

	const filterCourses = event => {
		event.preventDefault();
		if(searchButtonIsLoading)
			return;
		const formData = new FormData(event.target);
		setSearchParams({
			...searchParams,
			title: formData.get('searchCourseTitle')?.trim() || null,
			category_id: formData.get('searchCourseCategory') || null,
			course_type: formData.get('searchCourseType') || null,
			is_free: optionToBoolean(formData.get('searchCourseIsFree'), 'free', 'paid'),
			is_active: optionToBoolean(formData.get('searchCourseIsActive'), 'active', 'deactive'),
			is_special: optionToBoolean(formData.get('searchCourseIsSpecial'), 'special', 'normal'),
			sort: formData.get('searchCourseSort') || null,
			page: 1
		});
	};

	useEffect(() => {
		getCourseCategories();
	}, []);
	useEffect(() => {
		getCourses();
	}, [searchParams]);

	return (
		<>
			<Breadcrumb items={breadcrumbItems} />
			<div className="card mb-3">
				<div className="card-header">
					<h5 className="mb-0">{t('filter')} {t('courses')}</h5>
				</div>
				<div className="card-body bg-light">
					<form onSubmit={filterCourses}>
						<div className="row gx-2">
							<FormField fieldKey="searchCourseTitle" title={t('title')} isSearch />
							<FormField type="select" fieldKey="searchCourseCategory" title={t('category')} selectItems={courseCategories} isSearch />
							<FormField type="select" fieldKey="searchCourseType" title={t('course_type')} selectItems={courseTypeOptions(t)} isSearch />
							<FormField type="select" fieldKey="searchCourseIsFree" title={t('price')} selectItems={pricingList} isSearch />
							<FormField type="select" fieldKey="searchCourseIsActive" title={t('status')} selectItems={activationsList} isSearch />
							<FormField type="select" fieldKey="searchCourseIsSpecial" title={t('special')} selectItems={specialsList} isSearch />
							<FormField type="select" fieldKey="searchCourseSort" title={t('sorting')} selectItems={sortsList} isSearch required />
						</div>
						<div>
							<LoadingButton className="btn btn-primary d-inline-block mt-3 me-2" type="submit" name="search" isLoading={searchButtonIsLoading}>
								<i className="fa fa-search"></i> {t('search')}
							</LoadingButton>
						</div>
					</form>
				</div>
			</div>
			<ListTable title={t('courses')} pageNumber={searchParams.page} pageCounter={searchParams.count} totalCount={totalCount} data={coursesListData} />
			<Pagination pagesCount={Math.ceil(totalCount / searchParams.count)} action={page => setSearchParams({ ...searchParams, page })} />
		</>
	);
};

export default CoursesList;
